import React, { ReactElement, useCallback, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useTranslation } from 'react-i18next';
import { FREE_EMPLOYEE_CAP, IFreeTierLimitError } from '../../interfaces/plan.interface';

/**
 * The Free plan limit carried by an API error, or null when the error is anything else. Keys off the
 * machine `code` of a 403 body, never off the (translated) message.
 */
export function freeTierLimitOf(error: unknown): IFreeTierLimitError | null {
    const response = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
    if (response?.status !== 403) return null;
    const data = response.data as Partial<IFreeTierLimitError> | null | undefined;
    if (!data || typeof data !== 'object' || data.code !== 'FREE_TIER_LIMIT') return null;
    return data as IFreeTierLimitError;
}

interface DialogProps {
    limit: IFreeTierLimitError | null;
    onClose: () => void;
}

/** Explains which Free plan limit refused the action and who can lift it. */
export function FreeTierNudgeDialog({ limit, onClose }: DialogProps) {
    const { t } = useTranslation('common');
    const reason = (() => {
        if (!limit) return '';
        switch (limit.limit) {
            case 'EMPLOYEES': {
                const cap = limit.cap ?? FREE_EMPLOYEE_CAP;
                return t('freeTier.employees', { cap, current: limit.current ?? cap });
            }
            case 'DEVICE_SYNC':
                return t('freeTier.deviceSync');
            case 'MODULE':
                return t('freeTier.module');
            default:
                return limit.message;
        }
    })();
    // Modules are only switched on by the platform operator, who is the one reading a MODULE refusal.
    const nextStep = limit?.limit === 'MODULE' ? t('freeTier.upgradeFirst') : t('freeTier.upgrade');

    return (
        <Dialog open={limit !== null} onClose={onClose} maxWidth="xs" fullWidth closeAfterTransition={false}>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <WorkspacePremiumIcon color="primary" />
                    <span>{t('freeTier.title')}</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{reason}</Typography>
                <Typography variant="body2" color="text.secondary">{nextStep}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">{t('freeTier.ok')}</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Upgrade nudge for screens whose writes can hit a Free plan limit. In a catch block:
 *
 *     if (!freeTier.handle(err)) toast.showApiError(err, fallback);
 *
 * and render `freeTier.element` once in the screen.
 */
export function useFreeTierNudge(): { handle: (error: unknown) => boolean; element: ReactElement } {
    const [limit, setLimit] = useState<IFreeTierLimitError | null>(null);
    const handle = useCallback((error: unknown) => {
        const found = freeTierLimitOf(error);
        if (found) setLimit(found);
        return found !== null;
    }, []);
    const element = <FreeTierNudgeDialog limit={limit} onClose={() => setLimit(null)} />;
    return { handle, element };
}
